import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { getInfrastructureByLocationAction } from '@/redux/slices/dashboardSlice';
import { getMapsAction } from '@/redux/slices/mapSlice';
import MeteomaticsTokenService from '@/services/meteomaticsToken.js';
import type { DashboardContextValueInterface } from '@/contexts/DashboardContext';
import type { AppDispatch } from '@/redux/store';
import { getMapModeStrategy } from '../mapModes';

interface UseMapDataLoadingParams {
	contextValue: DashboardContextValueInterface;
	isDmaOverlayEnabled: boolean;
	onModeChange: () => void;
}

export function useMapDataLoading({ contextValue, isDmaOverlayEnabled, onModeChange }: UseMapDataLoadingParams) {
	const dispatch = useDispatch<AppDispatch>();
	const fetchDataRef = useRef<(() => Promise<void>) | null>(null);
	const loadMapInfoRef = useRef<(() => void) | null>(null);

	useEffect(() => {
		const initMeteomaticsToken = async () => {
			try {
				await MeteomaticsTokenService.getValidToken();
			} catch (error) {
				console.error('Error initializing Meteomatics token:', error);
			}
		};
		initMeteomaticsToken();
	}, []);

	const loadMapInfoInternal = useCallback(() => {
		dispatch(getMapsAction(contextValue.selectedLocation));
	}, [contextValue.selectedLocation]);

	loadMapInfoRef.current = loadMapInfoInternal;
	const loadMapInfo = useCallback(() => {
		if (loadMapInfoRef.current) {
			loadMapInfoRef.current();
		}
	}, []);

	const fetchDataInternal = useCallback(async () => {
		const currentMapContext = {
			selectedLocation: contextValue.selectedLocation,
			selectedSensors: contextValue.selectedSensors,
			activatedDigitalTwinMode: contextValue.activatedDigitalTwinMode,
			activatedInflowMode: contextValue.activatedInflowMode,
			activatedDmaMode: isDmaOverlayEnabled,
			activatedSewerAnalysisMode: contextValue.activatedSewerAnalysisMode,
			selectedDate: contextValue.selectedDate,
			activatedMmsMode: contextValue.activatedMmsMode,
			selectedMmsId: contextValue.selectedMmsId,
			selectedMmsFileName: contextValue.selectedMmsFileName,
		};
		const currentStrategy = getMapModeStrategy(currentMapContext);
		const fetchParams = currentStrategy.getFetchParams(currentMapContext);

		dispatch(getInfrastructureByLocationAction(contextValue.selectedLocation, fetchParams.mode || ''));
	}, [
		contextValue.selectedLocation,
		contextValue.activatedDigitalTwinMode,
		contextValue.activatedInflowMode,
		isDmaOverlayEnabled,
		contextValue.activatedSewerAnalysisMode,
		contextValue.selectedDate,
		contextValue.activatedMmsMode,
		contextValue.selectedMmsId,
		contextValue.selectedMmsFileName,
	]);

	fetchDataRef.current = fetchDataInternal;
	const fetchData = useCallback(async () => {
		if (fetchDataRef.current) {
			await fetchDataRef.current();
		}
	}, []);

	useEffect(() => {
		if (contextValue.selectedLocation) {
			fetchData();
			loadMapInfo();
		}
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		onModeChange();
		if (contextValue.selectedLocation) {
			fetchData();
			loadMapInfo();
		}
	}, [
		contextValue.selectedLocation,
		contextValue.activatedInflowMode,
		contextValue.activatedDigitalTwinMode,
		contextValue.activatedMmsMode,
		contextValue.activatedSewerAnalysisMode,
		contextValue.fetchDashboardDataIndex,
		fetchData,
		loadMapInfo,
	]);
}
